import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Inbox, FileText, CalendarCheck, Truck, Receipt, CreditCard, Star } from "lucide-react";
import { EASE } from "./Reveal";

export const PIPELINE_NODES = [
  { id: "lead", code: "01", label: "Lead Capture", icon: Inbox, note: "Web form, call, or text lands in one log. Auto-reply fires in seconds." },
  { id: "estimate", code: "02", label: "Fast Estimate", icon: FileText, note: "Template + pricing matrix turns a site visit into a quote in under an hour." },
  { id: "schedule", code: "03", label: "Smart Schedule", icon: CalendarCheck, note: "Job books into the ideal-week calendar. Confirmations and reminders send themselves." },
  { id: "dispatch", code: "04", label: "Do the Work", icon: Truck, note: "Crew (or you) rolls with the address, access notes, and scope in hand." },
  { id: "invoice", code: "05", label: "Same-Day Invoice", icon: Receipt, note: "Job complete triggers the invoice before the truck leaves the driveway." },
  { id: "payment", code: "06", label: "Auto-Payment", icon: CreditCard, note: "Card or bank link on every invoice. Money lands without a chase." },
  { id: "review", code: "07", label: "Review & Retain", icon: Star, note: "24 hours later: review request out, rebook reminder queued." },
];

export default function WorkflowPipeline({ dark = true, auto = true }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => setActive((a) => (a + 1) % PIPELINE_NODES.length), 2600);
    return () => clearInterval(t);
  }, [auto]);

  const node = PIPELINE_NODES[active];
  const Icon = node.icon;

  return (
    <div data-testid="workflow-pipeline" className="w-full">
      <div className="flex flex-col lg:flex-row gap-3 lg:gap-0">
        {PIPELINE_NODES.map((n, i) => {
          const NIcon = n.icon;
          const isActive = i === active;
          return (
            <div key={n.id} className="flex-1 flex lg:flex-col items-stretch">
              <button
                data-testid={`pipeline-node-${n.id}`}
                onClick={() => setActive(i)}
                className={`flex-1 lg:w-full text-left p-3 lg:p-4 border transition-all duration-300 relative ${
                  isActive
                    ? dark
                      ? "border-safety bg-safety/10"
                      : "border-safety bg-white shadow-lg"
                    : dark
                      ? "border-white/10 bg-white/[0.03] hover:border-white/25"
                      : "border-slate-200 bg-white/60 hover:border-slate-400"
                }`}
              >
                <div className="flex lg:flex-col items-center lg:items-start gap-3">
                  <span className={`font-mono text-[10px] tracking-[0.18em] ${isActive ? "text-safety" : dark ? "text-slate-500" : "text-slate-400"}`}>
                    [{n.code}]
                  </span>
                  <NIcon size={18} className={isActive ? "text-safety" : dark ? "text-slate-400" : "text-slate-500"} />
                  <span className={`text-xs font-semibold leading-tight ${isActive ? (dark ? "text-white" : "text-navy") : dark ? "text-slate-400" : "text-slate-600"}`}>
                    {n.label}
                  </span>
                </div>
                {isActive && (
                  <motion.span
                    layoutId={dark ? "pipe-active-dark" : "pipe-active-light"}
                    className="absolute left-0 top-0 bottom-0 w-[3px] bg-safety"
                  />
                )}
              </button>
              {i < PIPELINE_NODES.length - 1 && (
                <div className={`hidden lg:block h-px self-center w-full ${dark ? "bg-white/10" : "bg-slate-300"}`} />
              )}
            </div>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={node.id}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.45, ease: EASE }}
          data-testid="pipeline-detail"
          className={`mt-5 p-5 border flex items-start gap-4 ${
            dark ? "border-white/10 bg-charcoal/80" : "border-slate-200 bg-white"
          }`}
        >
          <div className="w-11 h-11 shrink-0 grid place-items-center bg-safety text-white">
            <Icon size={20} />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-safety mb-1">
              NODE {node.code} // ACTIVE
            </p>
            <p className={`font-display font-bold ${dark ? "text-white" : "text-navy"}`}>{node.label}</p>
            <p className={`text-sm mt-1 leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>{node.note}</p>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
